import React from 'react';
import { Folder } from 'lucide-react';
import type { ResourceType } from '@cotera/api';
import { Text } from '@cotera/client/app/components/ui';
import {
  ResourceScopeBadge,
  ResourceOwnerBadge,
} from '../resources/list/resource-scope-badge';
import type { FolderScope } from '../resources/list/resource-list-types';
import type { WorkflowSummary } from '../workflow/use-workflow-summaries';
import {
  isVirtualChatsItem,
  type FolderContentsListItem,
} from './folder-contents-list-items';
import { getLibraryItemDisplay } from './library-item-display';
import { itemTypeLabel } from './library-list-row';
import { FavoriteRowMarker } from '../resources/favorites';

export type LibraryCardProps = {
  item: FolderContentsListItem;
  userId: string;
  nestedFolderScope: FolderScope | null;
  isTopLevel: boolean;
  workflowSummaries: Map<string, WorkflowSummary>;
  dateField: 'updatedAt' | 'createdAt';
  /** The type icon tile, rendered by the page (shared with list mode). */
  icon: React.ReactNode;
};

/**
 * Card form of a Library item: title row, a body that takes the slack, and a
 * meta footer.
 *
 * Carries the same facts as {@link LibraryRow} — both derive them from
 * `getLibraryItemDisplay` — but opts into descriptions, since only the grid has
 * room to give an agent or document two lines of summary.
 */
export const LibraryCard: React.FC<LibraryCardProps> = ({
  item,
  userId,
  nestedFolderScope,
  isTopLevel,
  workflowSummaries,
  dateField,
  icon,
}) => {
  const {
    name,
    summary,
    scope,
    users,
    ownerId,
    isInboundShare,
    dateLabel,
    hasUnreadAssistant,
  } = getLibraryItemDisplay({
    item,
    userId,
    nestedFolderScope,
    isTopLevel,
    workflowSummaries,
    dateField,
    includeDescription: true,
  });

  const scopeBadge =
    isInboundShare && ownerId !== undefined ? (
      <ResourceOwnerBadge ownerId={ownerId} />
    ) : scope !== null ? (
      // No `withPrefix` any more: the badge leads with a glyph in every surface,
      // so a card no longer needs a spelled-out "Shared with" that a column
      // header supplies elsewhere. It also means an unshared card now says
      // "Private" instead of rendering nothing, matching the rows beside it.
      <ResourceScopeBadge scope={scope} users={users} ownerId={ownerId} />
    ) : null;

  return (
    <>
      {/* Title row. `pr-12` clears the ⋯/checkbox pinned top-right — they're
          absolutely positioned, so the text needs the gap reserved even while
          they're hidden, or names would reflow on hover. */}
      <div className="flex items-center gap-2.5 min-w-0 pr-12">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center">
          {icon}
        </span>
        {/* `title` on the wrapper, not the paragraph: `Text.P` takes no DOM
            attributes, and a truncated name is otherwise unreadable. The star
            sits inside the wrapper so it trails the name — see the note in
            {@link LibraryRow}. */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5" title={name}>
          <Text.P className="min-w-0 truncate font-medium text-slate-800">
            {name}
          </Text.P>
          {isVirtualChatsItem(item) ? null : (
            <FavoriteRowMarker
              type={item.type as ResourceType}
              id={item.data.id}
            />
          )}
        </div>
        {/* Type is carried by the icon tile alone now, and the tile has no
            accessible name — so the card says it out loud. */}
        <span className="sr-only">{itemTypeLabel(item)}</span>
        {hasUnreadAssistant ? (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-primary"
            aria-label="Unread assistant message"
          />
        ) : null}
      </div>

      {/* Body takes the slack, so the footer pins to the bottom edge instead of
          leaving a ragged gap mid-card when the summary is short. */}
      <div className="mt-2 min-w-0 flex-1">
        {summary !== null ? (
          summary.kind === 'run' ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${summary.dotClass}`}
                aria-hidden="true"
              />
              <span className="truncate">{summary.text}</span>
            </p>
          ) : summary.kind === 'location' ? (
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
              <Folder className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{summary.text}</span>
            </p>
          ) : (
            // Two lines: enough to convey a description, bounded so every card
            // in the row stays the same height.
            <p
              className="line-clamp-2 text-xs leading-relaxed text-muted-foreground"
              title={summary.text}
            >
              {summary.text}
            </p>
          )
        ) : null}
      </div>

      {/* Meta footer, separated by a hairline: who can see this, and when it
          last moved. The type used to lead this line; it now rides the icon
          tile in the title row, which says the same thing without spending a
          line of grey text on it. */}
      <div className="mt-2 flex items-center justify-between gap-2 min-w-0 border-t border-border/50 pt-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {scopeBadge !== null ? (
            <span className="min-w-0 truncate">{scopeBadge}</span>
          ) : null}
        </div>
        <Text.Caption className="shrink-0 text-muted-foreground tabular-nums whitespace-nowrap">
          {dateLabel}
        </Text.Caption>
      </div>
    </>
  );
};
